/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: authoring-common
 * (C) Copyright IBM Corp. 2020, 2021
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
	Accordion,
	AccordionItem,
	OverflowMenu,
	OverflowMenuItem,
	Button
} from '@ba/ba-ui-carbon';
import {
	FlexLayout,
	FlexItem,
	Label,
	SVGIcon
} from '@ba/ba-ui-carbon-toolkit';

import StringResources from 'nls/StringResources';
import CustomPalette from '../../dialog/CustomPalette';
import DialogWrapper from '../../utilities/DialogWrapper';
import PaletteHelper from '../../utilities/PaletteHelper';
import backIcon from '@ba/ba-graphics/dist/icons-js/arrow--left_16.js';
import addIcon from '@ba/ba-graphics/dist/icons-js/add_32.js';
class PaletteListing extends Component{
	// these are the anticipated props based on the ChangePaletteView options
	static propTypes = {
		paletteService: PropTypes.object.isRequired, // was glass context
		getPalettes: PropTypes.func.isRequired,
		createPalette: PropTypes.func,
		headerText: PropTypes.string,
		onChange: PropTypes.func.isRequired,
		/** callback function to call when a palette get updated */
		onUpdatePalette: PropTypes.func.isRequired,
		/** callback function to call when a new custom palette get created */
		onCreatePalette: PropTypes.func.isRequired,
		/** callback function to call when a palette get deleted */
		onDeletePalette: PropTypes.func.isRequired,
		onClose: PropTypes.func,
		mruComponentIdentifier: PropTypes.string.isRequired, // was component
		selectedId: PropTypes.string,
		createPaletteType: PropTypes.string,
		showRecentlyUsed: PropTypes.bool,
		createGlobalPalettes: PropTypes.bool, // was isAdminUI
		sectionMenuItems: PropTypes.object // was menuItems
	}

	static defaultProps = {
		sectionMenuItems: {
			'custom': ['edit', 'duplicate', 'delete'],
			'local': ['duplicate'],
			'global': ['duplicate'],
			'system': ['duplicate'],
			'legacy': []
		},
		headerText: StringResources.get('manageColorPalettes'),
		onUpdatePalette: () => {},
		onCreatePalette: () => {},
		onDeletePalette: () => {}
	}

	constructor(props) {
		super(props);
		const { selectedId } = this.props;
		this.state = {
			palettes: null,
			selectedId: selectedId
		};
		const { getPalettes, paletteService } = this.props;
		this.paletteHelper = new PaletteHelper({ paletteService,  getPalettesFn: getPalettes });
	}

	componentDidUpdate() {
		const { selectedId: propSelectedId } = this.props;
		const { selectedId: stateSelectedId } = this.state;
		if (propSelectedId != stateSelectedId) {
			this.setState({ selectedId: propSelectedId });
		}
	}

	componentDidMount(){
		this._registerPaletteServiceEvents();
		this.paletteHelper.getPaletteGroupBySections()
			.then(palettes => {
				this.setState({
					palettes: palettes
				});
			});
	}

	componentWillUnmount(){
		this._unregisterPaletteServiceEvents();
	}

	render(){
		if ( !this.state.palettes ) return null;
		const sections = [];
		const sectionOrder = this.paletteHelper.getPaletteSectionOrder();
		sectionOrder.forEach(section => {
			let open = false;
			let palettes = this.state.palettes[section].map(( palette ) => {
				let fills = palette.content.fills.map( ( fill ) => {
					if ( this.props.createPaletteType === 'continuous' ){
						fill = fill.fill;
					}
					return (<FlexItem className='clsPaletteListingBoxStyle' style={{ backgroundColor: fill }} key={fill}/>);
				});
				let className = 'clsPaletteListingPalette';
				if ( palette.id === this.state.selectedId ) {
					className += '_selected';
					open = true;
				}
				const menuItems = this.props.sectionMenuItems[ section ];
				let overFlow = null;
				if ( menuItems && menuItems.length ) {
					overFlow = (
						<FlexItem>
							<OverflowMenu flipped={true} onClick={(event) => event.stopPropagation()}>
								{ menuItems.map( (menuItem) => {
									return (
										<OverflowMenuItem
											key={menuItem}
											itemText={StringResources.get(menuItem)}
											onClick={ ( event ) => this._performPaletteAction( event, palette, menuItem )}
										/>);
								})}
							</OverflowMenu>
						</FlexItem>
					);
				}
				return (
					<div key={palette} className={className} onClick={this._selectPalette.bind(this, palette)}>
						<FlexLayout justifyContent='space-between' alignItems='center'>
							<FlexItem>
								<Label label={palette.content.label} className='clsPaletteListingPalette_paletteLabel' />
							</FlexItem>
							{overFlow}
						</FlexLayout>
						<FlexLayout wrap='wrap'>
							{fills}
						</FlexLayout>
					</div>
				);
			});
			if ( palettes.length ){
				sections.push( (
					<AccordionItem open={open} title={StringResources.get(section + 'PaletteLabel')} className='clsPaletteListingAccordianItem'>
						{palettes}
					</AccordionItem>
				));
			}
		});
		let close = null;
		if ( this.props.onClose ){
			close = ( <FlexItem>
				<Button
					renderIcon={() => <SVGIcon
						icon={backIcon}
						className="clsPaletteListing_buttonSvg"
						height={16}
						width={16}
					/>}
					onClick={this.props.onClose}
					iconDescription={StringResources.get('back')}
					hasIconOnly
					kind="ghost"
					size="small"
					style={{ marginLeft: '12px' }}
				/>
			</FlexItem> );
		}
		let createPalette = null;
		if ( this.props.createPalette ){
			createPalette = (
				<FlexItem>
					<Button
						renderIcon={() => <SVGIcon
							icon={addIcon}
							className="clsPaletteListing_buttonSvg"
							height={16}
							width={16}
						/>}
						onClick={this.props.createPalette}
						iconDescription={StringResources.get('createPalette')}
						hasIconOnly
						kind="ghost"
						size="small"
						tooltipAlignment="end"
						style={{ marginRight: '8px' }}
					/>
				</FlexItem>
			);
		}
		return (
			<FlexLayout direction="column" width="100%">
				{close}
				<FlexItem>
					<FlexLayout justifyContent="space-between">
						<FlexItem>
							<Label label={ this.props.headerText } className='clsPaletteListingPalette_headerLabel'/>
						</FlexItem>
						{createPalette}
					</FlexLayout>
				</FlexItem>
				<FlexItem>
					<Accordion>
						{sections}
					</Accordion>
				</FlexItem>
			</FlexLayout>
		);
	}

	_registerPaletteServiceEvents() {
		if (!this._registered) {
			this.props.paletteService.on('palette:created', this._onPaletteCreated.bind(this), this);
			this.props.paletteService.on('palette:updated', this._onPaletteUpdate.bind(this), this);
			this.props.paletteService.on('palette:deleted', this._onPaletteDelete.bind(this), this);
			this._registered = true;
		}
	}

	_unregisterPaletteServiceEvents() {
		if (this._registered) {
			this.props.paletteService.off('palette:created', this._onPaletteCreated, this);
			this.props.paletteService.off('palette:updated', this._onPaletteUpdate, this);
			this.props.paletteService.off('palette:deleted', this._onPaletteDelete, this);
		}
	}

	_selectPalette( palette ){
		this.props.onChange(palette);
	}

	_onPaletteUpdate( options = {} ) {
		const { selectedId } = this.state;
		if (this.paletteHelper.isSamePaletteId(options.paletteId, selectedId)) {
			const { onUpdatePalette } = this.props;
			const palette = this.paletteHelper.getPalette(selectedId);
			onUpdatePalette(palette);
		}
		this.paletteHelper.getPaletteGroupBySections()
			.then(palettes => {
				this.setState({
					palettes: palettes
				});
			});
	}

	_onPaletteDelete( options ){
		// @Brad not sure what is the goal here no not changing code
		const palettes = { ...this.state.palettes };
		let matchedSection = null;
		for ( let sectionName in palettes ){
			const sectionPalettes = palettes[sectionName];
			const matchedPalette = sectionPalettes.find( (candidatePalette) => {
				if (PaletteHelper.isCustomPalette(candidatePalette.id)) {
					return candidatePalette.id == PaletteHelper.getCustomPaletteId(options.paletteId);
				}
				return 	candidatePalette.id == options.paletteId;
			});

			if ( matchedPalette ) {
				palettes[sectionName] = sectionPalettes.filter( (p) => p != matchedPalette );
				matchedSection = sectionName;
				break;
			}
		}
		if ( options.paletteId != this.state.selectedId ){
			this.setState( { palettes: palettes });
		} else {
			const section = matchedSection || 'system';
			const palette = palettes[section].length ? palettes[section][0] : palettes['system'][0];
			this.setState( { palettes: palettes, selectedId: palette.id });
		}
		this.props.onDeletePalette(options.paletteId);
	}

	_onPaletteCreated(options = {}) {
		return this.paletteHelper.refreshPalette()
			.then(newPaletteList => {
				const newCMPaletteId = PaletteHelper.getCustomPaletteId(options.paletteId);
				const newPalette = this.paletteHelper.getPalette(newCMPaletteId);
				this.setState({
					palettes: newPaletteList,
					selectedId: newCMPaletteId
				});
				const { onCreatePalette } = this.props;
				onCreatePalette(newCMPaletteId, newPalette);
			});
	}

	_performPaletteAction( _event, palette, action ){
		_event.stopPropagation();
		switch( action ){
			case 'edit':
				DialogWrapper.show( CustomPalette, {
					paletteService: this.props.paletteService,
					paletteDef: palette.content,
					paletteId: palette.id,
					isDialogOpen: true,
					type: this.props.createGlobalPalettes ? 'global' : '',
					userClass: this.props.createGlobalPalettes ? 'admin' : '',
					paletteType: palette.content.type === 'continuous' ? 'continuous' : 'standard'
				});
				break;
			case 'delete':
				this.props.paletteService.deletePalette( palette.id );
				break;
			case 'duplicate':
				DialogWrapper.show( CustomPalette, {
					paletteService: this.props.paletteService,
					paletteDef: palette.content,
					isDialogOpen: true,
					type: this.props.createGlobalPalettes ? 'global' : '',
					userClass: this.props.createGlobalPalettes ? 'admin' : '',
					paletteType: palette.content.type === 'continuous' ? 'continuous' : 'standard'
				});
				break;
			default:
				break;
		}
	}
}

export default observer(PaletteListing);



// WEBPACK FOOTER //
// ./src/js/components/paletteListing/PaletteListing.js